import type { ExamCardComponent, ExamItemConfig } from 'config-types';
import { isDropdownComponent, isMultiSelectComponent } from '../ottehr-config/examination/examination.schema';
import type { ExamObservationComponentDTO, ExamObservationDTO } from '../types/api/chart-data/chart-data.types';

export interface ExamObservationItem {
  field: string;
  label: string;
  abnormal: boolean;
}

type ExamObservationMap = Record<string, ExamObservationDTO>;

const formatElementName = (elementName: string): string => {
  return elementName
    .split('-')
    .map((word) => {
      return word
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    })
    .join(' | ');
};

export function extractObservationsFromExamComponents(
  components: Record<string, ExamCardComponent>,
  section: 'normal' | 'abnormal',
  examObservations: ExamObservationMap,
  columnLabel?: string
): ExamObservationItem[] {
  const items: ExamObservationItem[] = [];

  Object.entries(components).forEach(([fieldName, component]) => {
    if (component.type === 'text') return;

    switch (component.type) {
      case 'checkbox': {
        const observation = examObservations[fieldName];
        if (observation && typeof observation.value === 'boolean' && observation.value === true) {
          items.push({
            field: fieldName,
            label: columnLabel ? `${columnLabel}: ${component.label}` : component.label,
            abnormal: section === 'abnormal',
          });
        }
        break;
      }

      case 'dropdown': {
        if (isDropdownComponent(component)) {
          Object.entries(component.components).forEach(([optionName, option]) => {
            const observation = examObservations[optionName];
            if (observation && typeof observation.value === 'boolean' && observation.value === true) {
              const baseLabel = columnLabel ? `${columnLabel}: ${component.label}` : component.label;
              items.push({
                field: optionName,
                label: `${baseLabel}: ${option.label}`,
                abnormal: section === 'abnormal',
              });
            }
          });
        }
        break;
      }

      case 'form': {
        Object.entries(component.components).forEach(([elementName]) => {
          const observation = examObservations[elementName];
          if (observation && typeof observation.value === 'boolean' && observation.value === true) {
            const baseLabel = columnLabel ? `${columnLabel}: ${component.label}` : component.label;
            const formattedElementName = formatElementName(elementName);
            const note = observation.note ? ` | ${observation.note}` : '';

            items.push({
              field: elementName,
              label: `${baseLabel}: ${formattedElementName}${note}`,
              abnormal: section === 'abnormal',
            });
          }
        });
        break;
      }

      case 'multi-select': {
        if (isMultiSelectComponent(component)) {
          const componentLabel = columnLabel ? `${columnLabel}: ${component.label}` : component.label;
          const selectedOptions: ExamObservationItem[] = [];
          Object.entries(component.options).forEach(([optionName, option]) => {
            const observation = examObservations[optionName];
            if (observation && typeof observation.value === 'boolean' && observation.value === true) {
              const description = option.description ? ` (${option.description})` : '';
              selectedOptions.push({
                field: optionName,
                label: `${componentLabel}: ${option.label}${description}`,
                abnormal: section === 'abnormal',
              });
            }
          });
          const observation = examObservations[fieldName];
          if (observation && observation.value === true && selectedOptions.length === 0) {
            items.push({
              field: fieldName,
              label: columnLabel ? `${columnLabel}: ${component.label}` : component.label,
              abnormal: section === 'abnormal',
            });
          }
          items.push(...selectedOptions);
        }
        break;
      }

      case 'column': {
        const nestedItems = extractObservationsFromExamComponents(
          component.components,
          section,
          examObservations,
          component.label
        );
        items.push(...nestedItems);
        break;
      }

      case 'checkbox-with-modal': {
        const observation = examObservations[fieldName];
        if (!observation || observation.value !== true) break;

        if (!observation.components?.length) {
          items.push({
            field: fieldName,
            label: component.label,
            abnormal: section === 'abnormal',
          });
          break;
        }

        let abnormalContained = false;

        const grouped = groupComponents(observation.components, (isAbnormal: boolean | undefined) => {
          if (isAbnormal || isAbnormal === undefined) abnormalContained = true;
        });

        const consolidatedLabel = formatGroupedComponents(grouped);
        const observationLabel = observation.label ? `${observation.label}: ` : '';

        items.push({
          field: fieldName,
          label: `${observationLabel}${consolidatedLabel}`,
          abnormal: abnormalContained,
        });

        break;
      }

      default:
        break;
    }
  });

  return items;
}

export function collectKnownExamFields(examConfig: ExamItemConfig): Set<string> {
  const knownFields = new Set<string>();

  const collectFromComponents = (components: Record<string, ExamCardComponent>): void => {
    Object.entries(components).forEach(([key, component]) => {
      if (component.type === 'checkbox' || component.type === 'checkbox-with-modal') {
        knownFields.add(key);
      } else if (component.type === 'text') {
        knownFields.add(key);
      } else if (component.type === 'dropdown') {
        knownFields.add(key);
        if (isDropdownComponent(component)) {
          Object.keys(component.components).forEach((k) => knownFields.add(k));
        }
      } else if (component.type === 'column') {
        collectFromComponents(component.components);
      } else if (component.type === 'multi-select') {
        knownFields.add(key);
        if (isMultiSelectComponent(component)) {
          Object.keys(component.options).forEach((k) => knownFields.add(k));
        }
      } else if (component.type === 'form') {
        Object.keys(component.components).forEach((k) => knownFields.add(k));
      }
    });
  };

  Object.values(examConfig).forEach((section) => {
    collectFromComponents(section.components.normal);
    collectFromComponents(section.components.abnormal);
    Object.keys(section.components.comment).forEach((k) => knownFields.add(k));
  });

  return knownFields;
}

// helpers for formatting labels for checkbox-with-modal
function groupComponents(
  components: ExamObservationComponentDTO[],
  onAbnormal: (abnormal: boolean | undefined) => void
): Map<string | null, Map<string, string[]>> {
  const result = new Map<string | null, Map<string, string[]>>();

  components
    .filter((c) => c.value)
    .forEach((c) => {
      const columnKey = c.columnLabel || null;
      const groupKey = c.groupLabel || '';

      if (!result.has(columnKey)) {
        result.set(columnKey, new Map());
      }

      const groupMap = result.get(columnKey)!;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }

      groupMap.get(groupKey)!.push(c.label);

      onAbnormal(c.abnormal);
    });

  return result;
}

function formatGroupedComponents(grouped: Map<string | null, Map<string, string[]>>): string {
  return Array.from(grouped.entries())
    .sort(([a], [b]) => (a ?? '').localeCompare(b ?? '')) // handles L coming before R
    .map(([columnLabel, groupMap]) => {
      const groups = Array.from(groupMap.entries())
        .map(([groupLabel, labels]) => (groupLabel ? `${groupLabel}: ${labels.join(', ')}` : labels.join(', ')))
        .join('; ');

      return columnLabel ? `${columnLabel}: ${groups}` : groups;
    })
    .join('; ');
}
